<template>
	<v-card dark class="audio-timeline-card pa-4" elevation="4" tabindex="0">
		<AudioToolbar
			:canPlay="canPlay"
			:isPlaying="isPlaying"
			:currentTimeFormatted="currentTimeFormatted"
			:zoomAt="zoomAt"
			:fitView="fitView"
			:togglePlayback="togglePlayback"
		></AudioToolbar>

		<div ref="timelineContainer" class="timeline-visual-wrapper" @click="handleSeek">
			<v-progress-linear
				v-if="isLoading"
				indeterminate
				color="primary"
				class="ma-0 absolute-center"
			></v-progress-linear>

			<canvas ref="waveformCanvas" class="waveform-canvas"></canvas>

			<div class="playhead-line" :style="{ left: playheadLeft }"></div>

			<div v-if="error" class="error-overlay d-flex align-center justify-center">
				<v-icon color="error" class="mr-2">error</v-icon>
				<span class="body-2">{{ error }}</span>
			</div>
		</div>

		<AudioScrollbar
			:audioDuration="audioDuration"
			:updateKeyForSyncScrollbar="updateKeyForSyncScrollbar"
			:viewportStartSec="viewportStartSec"
			:viewportEndSec="viewportEndSec"
			@viewport-change="updateViewportAndRedraw"
		></AudioScrollbar>

		<v-layout row justify-space-between class="mt-1 px-1">
			<span class="caption grey--text">{{ formatTime(viewportStartSec) }}</span>
			<span class="caption grey--text">{{ formatTime(viewportEndSec) }}</span>
		</v-layout>
	</v-card>
</template>

<script>
import { AudioWave, MIN_PEAKS_RESOLUTION, PEAKS_RESOLUTION_FACTOR } from './AudioWave';
import AudioToolbar from './AudioToolbar.vue';
import AudioScrollbar from './AudioScrollbar.vue';

const DEFAULT_MIN_CANVAS_WIDTH = 512;
const FALLBACK_CANVAS_WIDTH = 1024;
const INITIAL_ZOOM_LEVEL = 1;
const MIN_ZOOM_LEVEL = 1;
const MAX_ZOOM_LEVEL = 256;
const INITIAL_VIEWPORT_START_SEC = 0;

export default {
	name: 'AudioBlockWaveform',
	components: { AudioScrollbar, AudioToolbar },
	props: {
		audioUrl: { type: String, required: true },
		amplitudeScale: { type: Number, default: 0.85 }
	},
	data() {
		return {
			isPlaying: false,
			currentTime: 0,
			isLoading: false,
			error: null,
			startTime: 0,
			offsetTime: 0,
			animationFrameId: null,
			zoom: INITIAL_ZOOM_LEVEL,
			minZoom: MIN_ZOOM_LEVEL,
			maxZoom: MAX_ZOOM_LEVEL,
			viewportStartSec: 0,
			viewportEndSec: 0,
			isPanning: false,
			panLastX: 0,
			lastTimeUpdate: 0,
			devicePixelRatio: 1,
			resizeObserver: null,
			updateKeyForSyncScrollbar: Math.random(),
			audioDuration: 0
		};
	},
	computed: {
		hasAudioBuffer() {
			return !!this.audioWave.buffer;
		},
		canPlay() {
			return !this.isLoading && this.hasAudioBuffer;
		},
		currentTimeFormatted() {
			return this.formatTime(this.currentTime);
		},
		playheadLeft() {
			const visible = this.viewportEndSec - this.viewportStartSec || 1;
			const relation = (this.currentTime - this.viewportStartSec) / visible;
			const percentage = Math.max(0, Math.min(1, relation)) * 100;

			return percentage + '%';
		}
	},
	watch: {
		audioUrl: {
			immediate: true,
			async handler(newUrl) {
				if (newUrl) {
					this.audioWave.initContext();
					await this.loadAudioFile(newUrl);
				}
			}
		}
	},
	beforeCreate() {
		this.audioWave = new AudioWave();
	},
	mounted() {
		this.addListeners();
	},
	beforeDestroy() {
		this.removeListeners();
	},
	methods: {
		setTimelineContainerListeners() {
			const timelineContainer = this.$refs.timelineContainer;
			if (!timelineContainer) {
				return;
			}

			timelineContainer.addEventListener('wheel', this.handleWheel, { passive: false });
			timelineContainer.addEventListener('mousedown', this.handleMouseDown);

			this.resizeObserver = new ResizeObserver(entries => {
				for (const entry of entries) {
					const newWidth = Math.floor(entry.contentRect.width);
					if (newWidth > 0) {
						this.$nextTick(() => this.handleResize());
					}
				}
			});
			this.resizeObserver.observe(timelineContainer);
		},
		addListeners() {
			window.addEventListener('resize', this.handleResize);
			this.$nextTick(() => this.handleResize());

			this.setTimelineContainerListeners();

			this.$el.addEventListener('keydown', this.handleKeyDown);
			window.addEventListener('mousemove', this.handleMouseMove);
			window.addEventListener('mouseup', this.handleMouseUp);
		},
		removeTimelineContainerListeners() {
			const timelineContainer = this.$refs.timelineContainer;
			if (timelineContainer) {
				timelineContainer.removeEventListener('wheel', this.handleWheel);
				timelineContainer.removeEventListener('mousedown', this.handleMouseDown);
			}

			if (this.resizeObserver) {
				this.resizeObserver.disconnect();
				this.resizeObserver = null;
			}
		},
		removeListeners() {
			window.removeEventListener('resize', this.handleResize);
			this.stopPlayback();
			if (this.audioWave.ctx) {
				try {
					this.audioWave.ctx.close();
				} catch (e) {}
			}

			this.removeTimelineContainerListeners();

			this.$el.removeEventListener('keydown', this.handleKeyDown);
			window.removeEventListener('mousemove', this.handleMouseMove);
			window.removeEventListener('mouseup', this.handleMouseUp);
			cancelAnimationFrame(this.animationFrameId);
		},
		handleKeyDown(event) {
			event.preventDefault();

			if (event.target !== this.$el) return;

			const KeyCodeSpace = 'Space';
			if (event.code === KeyCodeSpace) this.togglePlayback();
		},
		async loadAudioFile(url) {
			this.isLoading = true;
			this.error = null;
			this.stopPlayback(true);

			try {
				await this.audioWave.loadFileFrom(url);
				this.audioDuration = this.audioWave.duration;

				this.afterLoadSetup();
			} catch (err) {
				console.error('AudioTimeline Error:', err);
				this.error = 'Error loading audio file';
			} finally {
				this.isLoading = false;
			}
		},
		afterLoadSetup() {
			this.viewportStartSec = INITIAL_VIEWPORT_START_SEC;
			this.viewportEndSec = this.audioWave.duration;
			this.zoom = INITIAL_ZOOM_LEVEL;

			this.handleResize();

			const canvas = this.$refs.waveformCanvas;
			const canvasCssWidth = canvas
				? Math.max(DEFAULT_MIN_CANVAS_WIDTH, Math.floor(canvas.clientWidth))
				: FALLBACK_CANVAS_WIDTH;
			const peaksResolution = Math.max(MIN_PEAKS_RESOLUTION, canvasCssWidth * PEAKS_RESOLUTION_FACTOR);
			this.audioWave.calculatePeaks(peaksResolution);

			const viewport = {
				startSec: this.viewportStartSec,
				endSec: this.viewportEndSec
			};

			this.draw(viewport);
		},
		draw(viewport) {
			this.audioWave.draw(this.$refs.waveformCanvas, this.devicePixelRatio, viewport);
		},
		formatTime(seconds) {
			if (isNaN(seconds)) return '00:00:000';
			const mins = Math.floor(seconds / 60);
			const secs = Math.floor(seconds % 60);
			const ms = Math.floor((seconds % 1) * 1000);
			return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${ms.toString().padStart(3, '0')}`;
		},
		togglePlayback() {
			const AUDIO_CTX_STATE_SUSPENDED = 'suspended';
			if (this.audioWave.ctx.state === AUDIO_CTX_STATE_SUSPENDED) {
				this.audioWave.ctx.resume();
			}

			if (this.isPlaying) {
				this.stopPlayback();
			} else {
				this.startPlayback(this.offsetTime);
			}
		},
		startPlayback(fromSeconds = 0) {
			if (!this.audioWave.buffer) return;

			this.audioWave.play(fromSeconds);
			this.offsetTime = fromSeconds;
			this.startTime = this.audioWave.ctx.currentTime;

			this.isPlaying = true;
			this.updateLoop();
			this.$emit('play', fromSeconds);
		},
		stopPlayback(reset = false) {
			this.audioWave.stop();

			if (reset) {
				this.offsetTime = 0;
				this.currentTime = 0;
			} else if (this.isPlaying) {
				this.offsetTime = this.audioWave.ctx.currentTime - this.startTime + this.offsetTime;
			}

			this.isPlaying = false;
			cancelAnimationFrame(this.animationFrameId);
			this.$emit('pause', this.currentTime);
		},
		updateLoop() {
			if (!this.isPlaying) return;
			const current = this.audioWave.ctx.currentTime - this.startTime + this.offsetTime;
			if (current >= this.audioWave.duration) {
				this.stopPlayback(true);
				this.$emit('ended');
				return;
			}
			const timestampMilliseconds = performance.now();
			if (!this.lastTimeUpdate || timestampMilliseconds - this.lastTimeUpdate > 30) {
				this.currentTime = current;
				this.$emit('timeupdate', current);
				this.lastTimeUpdate = timestampMilliseconds;

				const viewport = {
					startSec: this.viewportStartSec,
					endSec: this.viewportEndSec
				};

				this.draw(viewport);
			}
			this.animationFrameId = requestAnimationFrame(this.updateLoop);
			this.syncScrollbarAfterViewportChange();
		},
		handleSeek(event) {
			if (!this.audioWave.buffer) return;

			const rect = this.$refs.timelineContainer.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const ratio = Math.max(0, Math.min(1, x / rect.width));
			const newSeconds = this.viewportStartSec + ratio * (this.viewportEndSec - this.viewportStartSec);

			this.currentTime = newSeconds;
			this.offsetTime = newSeconds;

			if (this.isPlaying) {
				this.stopPlayback();
				this.startPlayback(newSeconds);
			} else {
				this.$emit('seek', newSeconds);
			}
		},
		handleResize() {
			const container = this.$refs.timelineContainer;
			const canvas = this.$refs.waveformCanvas;
			if (!container || !canvas) return;

			this.devicePixelRatio = window.devicePixelRatio || 1;
			const cssWidth = Math.max(1, Math.floor(container.clientWidth));
			const cssHeight = Math.max(1, Math.floor(container.clientHeight));

			canvas.style.width = cssWidth + 'px';
			canvas.style.height = cssHeight + 'px';
			canvas.width = cssWidth * this.devicePixelRatio;
			canvas.height = cssHeight * this.devicePixelRatio;

			if (this.audioWave.buffer) {
				const desiredRes = Math.max(MIN_PEAKS_RESOLUTION, cssWidth * PEAKS_RESOLUTION_FACTOR);
				if (!this.audioWave.peaks || this.audioWave.peaks.length < desiredRes / PEAKS_RESOLUTION_FACTOR) {
					this.audioWave.calculatePeaks(desiredRes);
				}
			}

			const viewport = {
				startSec: this.viewportStartSec,
				endSec: this.viewportEndSec
			};

			this.draw(viewport);
			this.syncScrollbarAfterViewportChange();
		},
		handleWheel(event) {
			event.preventDefault();

			const active = document.activeElement;
			if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;

			const rect = this.$refs.timelineContainer.getBoundingClientRect();
			const panPixels = event.deltaY || event.deltaX;
			const visibleSec = this.viewportEndSec - this.viewportStartSec || 1;
			const secPerPixel = visibleSec / rect.width;

			if (event.ctrlKey || event.metaKey) {
				const delta = event.deltaY > 0 ? 1.15 : 1 / 1.15;
				const anchorRatio = this.getZoomAnchorFromCursor();
				this.zoomAt(delta, anchorRatio);
			} else {
				const shift = panPixels * secPerPixel;
				this.panBy(shift);
			}
		},
		getZoomAnchorFromCursor() {
			const visible = this.viewportEndSec - this.viewportStartSec || 1;
			return (this.currentTime - this.viewportStartSec) / visible;
		},
		zoomAt(factor, anchorRatio) {
			const visible = this.viewportEndSec - this.viewportStartSec;
			const currentZoom = this.audioWave.duration / visible;
			let newZoom = currentZoom * factor;
			newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));

			const newVisible = this.audioWave.duration / newZoom;
			const anchor = Math.max(0, Math.min(1, anchorRatio));
			const centerSec = this.viewportStartSec + visible * anchor;

			let newStart = centerSec - newVisible * anchor;
			let newEnd = newStart + newVisible;

			if (newStart < 0) {
				newStart = 0;
				newEnd = newVisible;
			}
			if (newEnd > this.audioWave.duration) {
				newEnd = this.audioWave.duration;
				newStart = newEnd - newVisible;
			}

			this.viewportStartSec = newStart;
			this.viewportEndSec = newEnd;

			const viewport = { startSec: newStart, endSec: newEnd };
			this.draw(viewport);
			this.syncScrollbarAfterViewportChange();
		},
		panBy(seconds) {
			let newStart = this.viewportStartSec + seconds;
			let newEnd = this.viewportEndSec + seconds;
			const visible = this.viewportEndSec - this.viewportStartSec;

			if (newStart < 0) {
				newStart = 0;
				newEnd = visible;
			}

			if (newEnd > this.audioWave.duration) {
				newEnd = this.audioWave.duration;
				newStart = this.audioWave.duration - visible;
			}

			this.viewportStartSec = newStart;
			this.viewportEndSec = newEnd;

			const viewport = {
				startSec: this.viewportStartSec,
				endSec: this.viewportEndSec
			};

			this.draw(viewport);
			this.syncScrollbarAfterViewportChange();
		},
		handleMouseDown(event) {
			if (event.button !== 0) return;

			this.isPanning = true;
			this.panLastX = event.clientX;
		},
		handleMouseMove(event) {
			if (!this.isPanning) return;

			const dx = event.clientX - this.panLastX;
			this.panLastX = event.clientX;
			const rect = this.$refs.timelineContainer.getBoundingClientRect();
			const visibleSec = this.viewportEndSec - this.viewportStartSec;
			const secPerPixel = visibleSec / rect.width;
			this.panBy(-dx * secPerPixel);
		},
		handleMouseUp() {
			this.isPanning = false;
		},
		fitView() {
			this.zoom = INITIAL_ZOOM_LEVEL;
			this.viewportStartSec = INITIAL_VIEWPORT_START_SEC;
			this.viewportEndSec = this.audioWave.duration;

			const canvas = this.$refs.waveformCanvas;
			const cssWidth = canvas ? Math.floor(canvas.clientWidth) : FALLBACK_CANVAS_WIDTH;

			const resolution = Math.max(MIN_PEAKS_RESOLUTION, cssWidth * PEAKS_RESOLUTION_FACTOR);
			this.audioWave.calculatePeaks(resolution);

			const viewport = {
				startSec: this.viewportStartSec,
				endSec: this.viewportEndSec
			};

			this.draw(viewport);
		},
		syncScrollbarAfterViewportChange() {
			this.updateKeyForSyncScrollbar = Math.random();
		},
		updateViewportAndRedraw(viewport) {
			this.viewportStartSec = viewport.startSec;
			this.viewportEndSec = viewport.endSec;

			this.draw(viewport);
		}
	}
};
</script>

<style scoped>
.audio-timeline-card {
	background-color: #1a1a1a !important;
	border: 1px solid #333 !important;
}

.timeline-visual-wrapper {
	position: relative;
	height: 140px;
	background-color: #000;
	border-radius: 4px;
	border: 1px solid #444;
	overflow: hidden;
	cursor: crosshair;
	user-select: none;
}

.waveform-canvas {
	width: 100%;
	height: 100%;
	display: block;
}

.playhead-line {
	position: absolute;
	top: 0;
	bottom: 0;
	width: 2px;
	left: 0;
	transform: translateX(-50%);
	background-color: #ff5252;
	box-shadow: 0 0 10px rgba(255, 82, 82, 0.7);
	z-index: 5;
	pointer-events: none;
}

.absolute-center {
	position: absolute;
	top: 50%;
	left: 0;
	right: 0;
	transform: translateY(-50%);
	z-index: 10;
}

.error-overlay {
	position: absolute;
	inset: 0;
	background-color: rgba(0, 0, 0, 0.7);
	z-index: 20;
}

.timeline-visual-wrapper:hover::after {
	content: '';
	position: absolute;
	inset: 0;
	background-color: rgba(255, 255, 255, 0.05);
	pointer-events: none;
}

.audio-timeline-card:focus {
	outline: 2px solid rgba(33, 150, 243, 0.6);
	outline-offset: 2px;
}
</style>
